import { httpClient } from "@/shared/api/httpClient";
import { notificationContracts } from "@/entities/notification/api/contracts";
import {
    InboxQueryParams,
    NotificationItem,
    NotificationPage,
} from "@/entities/notification/model/types";
import { AxiosError } from "axios";

// ─── Types ────────────────────────────────────────────────────────────────────

export type { InboxQueryParams, NotificationItem, NotificationPage };

function emptyNotificationPage(): NotificationPage {
    return {
        content: [],
        pageable: {},
        totalPages: 0,
        totalElements: 0,
        last: true,
        size: 20,
        number: 0,
        sort: {},
        numberOfElements: 0,
        first: true,
        empty: true,
    };
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const notificationService = {
    /**
     * Lista notificações da inbox do usuário autenticado.
     * O backend identifica o usuário pelo `sub` do JWT.
     */
    async getInbox(params: InboxQueryParams = {}): Promise<NotificationPage> {
        try {
            return await httpClient.request(notificationContracts.inbox, { query: params });
        } catch (error) {
            const status = (error as AxiosError)?.response?.status;
            const hasSort = typeof params.sort === "string" && params.sort.length > 0;

            // Some notification backends break on sortable pageable fields; retry once without sort.
            if (status === 500 && hasSort) {
                const { sort, ...retryParams } = params;
                try {
                    return await httpClient.request(notificationContracts.inbox, { query: retryParams });
                } catch (retryError) {
                    const retryStatus = (retryError as AxiosError)?.response?.status;
                    if (retryStatus === 500) {
                        return emptyNotificationPage();
                    }
                    throw retryError;
                }
            }

            // Keep UI stable when notification service is unhealthy.
            if (status === 500) {
                return emptyNotificationPage();
            }

            throw error;
        }
    },

    /**
     * Marca uma notificação como lida.
     * Retorna a notificação atualizada.
     */
    async markAsViewed(notificationId: string): Promise<NotificationItem> {
        return httpClient.request(notificationContracts.markAsViewed(notificationId));
    },

    /**
     * Oculta (desabilita) uma notificação — resposta 204 No Content.
     */
    async disableNotification(notificationId: string): Promise<void> {
        await httpClient.request(notificationContracts.disable(notificationId));
    },
};
